import React, { useState, useEffect } from 'react';
import { Calendar, Download, CheckCircle, AlertCircle, RefreshCw, Plus, CalendarPlus, Edit, Trash2, Minus } from 'lucide-react';
import { format, addYears, isSameDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useHolidays, Holiday } from '../contexts/HolidayContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useDateFormat } from '../contexts/DateFormatContext';
import { useCountry } from '../contexts/CountryContext';
import { useSchedule } from '../contexts/ScheduleContext';
import { nationalHolidays } from '../data/nationalHolidays';
import { db } from '../firebase';
import { collection, addDoc, deleteDoc, query, where, getDocs } from 'firebase/firestore';

export function HolidayIntegration() {
  const { t } = useLanguage();
  const { formatDate } = useDateFormat();
  const { country } = useCountry();
  const { holidays: firebaseHolidays, addHolidayToCalendar, removeHolidayFromCalendar, isLoading } = useHolidays();
  const [localHolidays, setLocalHolidays] = useState<Holiday[]>([]);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showCustomHolidayForm, setShowCustomHolidayForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [customHolidayForm, setCustomHolidayForm] = useState({
    name: '',
    date: '',
    description: '',
    type: 'local' as 'national' | 'regional' | 'local'
  });

  // Obtener feriados nacionales según el país seleccionado
  const getFixedHolidays = (year: number): Holiday[] => {
    const countryHolidays = nationalHolidays[country];
    if (countryHolidays) {
      return countryHolidays(year);
    }
    // Fallback a España si no se encuentra el país
    return nationalHolidays['ES'](year);
  };

  // Obtener el nombre del país para mostrar en el título
  const getCountryName = (countryCode: string): string => {
    const countryNames: { [key: string]: string } = {
      'ES': 'España',
      'AR': 'Argentina',
      'UY': 'Uruguay',
      'CL': 'Chile',
      'CO': 'Colombia',
      'PE': 'Perú',
      'MX': 'México',
      'US': 'Estados Unidos',
      'PT': 'Portugal',
      'DE': 'Alemania',
      'GB': 'Inglaterra',
      'IT': 'Italia',
      'FR': 'Francia',
      'AD': 'Andorra'
    };
    return countryNames[countryCode] || 'España';
  };

  // Función para calcular fechas variables (como Viernes Santo)
  const calculateEaster = (year: number): Date => {
    // Algoritmo de Gauss para calcular Pascua
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const n = Math.floor((h + l - 7 * m + 114) / 31);
    const p = (h + l - 7 * m + 114) % 31;
    
    return new Date(year, n - 1, p + 1);
  };

  const getVariableHolidays = (year: number): Holiday[] => {
    const easter = calculateEaster(year);
    const goodFriday = new Date(easter);
    goodFriday.setDate(easter.getDate() - 2);
    
    return [
      {
        id: `good-friday-${year}`,
        date: format(goodFriday, 'yyyy-MM-dd'),
        name: 'Viernes Santo',
        type: 'national',
        description: 'Viernes Santo',
        isRecurring: true,
        addedToCalendar: false,
        year: year
      }
    ];
  };

  const loadHolidays = async (year: number, forceSync = false) => {
    // Verificar si ya se sincronizó hoy
    const lastSyncKey = `holidays_sync_${year}_${format(new Date(), 'yyyy-MM-dd')}`;
    const lastSyncDate = localStorage.getItem(lastSyncKey);
    const today = format(new Date(), 'yyyy-MM-dd');
    
    // Solo sincronizar si no se ha sincronizado hoy o si se fuerza
    if (!forceSync && lastSyncDate === today && localHolidays.length > 0) {
      return; // Ya se sincronizó hoy
    }
    
    setIsLoadingLocal(true);
    
    try {
      // Simular carga de API (en una app real sería una llamada a una API de feriados)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const fixedHolidays = getFixedHolidays(year);
      const variableHolidays = getVariableHolidays(year);
      
      // Filtrar feriados duplicados
      const allHolidays = [...fixedHolidays, ...variableHolidays];
      const uniqueHolidays = allHolidays.filter((holiday, index, self) => 
        index === self.findIndex(h => h.id === holiday.id)
      );
      
        setLocalHolidays(uniqueHolidays);
        setLastSync(new Date().toISOString());
        
        // Guardar fecha de sincronización (hoy)
        localStorage.setItem(lastSyncKey, today);
    } catch (error) {
      console.error('Error loading holidays:', error);
    } finally {
      setIsLoadingLocal(false);
    }
  };

  useEffect(() => {
    loadHolidays(selectedYear);
  }, [selectedYear, country]);


  const handleAddCustomHoliday = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingHoliday) {
        // Editar feriado existente
        await addHolidayToCalendar({
          date: customHolidayForm.date,
          name: customHolidayForm.name,
          type: customHolidayForm.type,
          description: customHolidayForm.description,
          isRecurring: false,
          addedToCalendar: editingHoliday.addedToCalendar,
          year: selectedYear
        });
      } else {
        // Crear nuevo feriado
        await addHolidayToCalendar({
          date: customHolidayForm.date,
          name: customHolidayForm.name,
          type: customHolidayForm.type,
          description: customHolidayForm.description,
          isRecurring: false,
          addedToCalendar: true,
          year: selectedYear
        });

        // También agregar a storeExceptions
        await addDoc(collection(db, 'storeExceptions'), {
          date: customHolidayForm.date,
          isOpen: false, // Los feriados están cerrados por defecto
          openTime: null,
          closeTime: null,
          reason: customHolidayForm.name,
          isHolidayException: true // Marcar como excepción de feriado
        });
      }
      
      setCustomHolidayForm({ name: '', date: '', description: '', type: 'local' });
      setShowCustomHolidayForm(false);
      setEditingHoliday(null);
    } catch (error) {
      console.error('Error adding custom holiday:', error);
    }
  };

  const handleEditHoliday = (holiday: Holiday) => {
    // Solo permitir editar feriados personalizados (no nacionales)
    const isCustomHoliday = !localHolidays.some(localHoliday => 
      localHoliday.date === holiday.date && localHoliday.name === holiday.name
    );
    
    if (isCustomHoliday) {
      setEditingHoliday(holiday);
      setCustomHolidayForm({
        name: holiday.name,
        date: holiday.date,
        description: holiday.description || '',
        type: holiday.type
      });
      setShowCustomHolidayForm(true);
    }
  };

  const handleDeleteHoliday = async (holiday: Holiday) => {
    // Solo permitir eliminar feriados personalizados
    const isCustomHoliday = !localHolidays.some(localHoliday => 
      localHoliday.date === holiday.date && localHoliday.name === holiday.name
    );
    
    if (isCustomHoliday && window.confirm(`¿Estás seguro de que quieres eliminar el feriado "${holiday.name}"?`)) {
      try {
        // Eliminar de Firebase holidays
        const firebaseHoliday = firebaseHolidays.find(fh => fh.id === holiday.id);
        if (firebaseHoliday) {
          await removeHolidayFromCalendar(firebaseHoliday.id);
        }

        // También eliminar de storeExceptions
        const q = query(
          collection(db, 'storeExceptions'), 
          where('date', '==', holiday.date), 
          where('reason', '==', holiday.name)
        );
        const snapshot = await getDocs(q);
        snapshot.forEach(async (doc) => {
          await deleteDoc(doc.ref);
        });
      } catch (error) {
        console.error('Error deleting holiday:', error);
      }
    }
  };

  const handleToggleCalendar = async (holiday: Holiday) => {
    try {
      if (holiday.addedToCalendar) {
        // Si ya está en el calendario, removerlo (pero no eliminar el feriado)
        const firebaseHoliday = firebaseHolidays.find(fh => fh.date === holiday.date && fh.name === holiday.name);
        if (firebaseHoliday) {
          await removeHolidayFromCalendar(firebaseHoliday.id);
          
          // También remover de storeExceptions
          const q = query(
            collection(db, 'storeExceptions'), 
            where('date', '==', holiday.date), 
            where('reason', '==', holiday.name)
          );
          const snapshot = await getDocs(q);
          snapshot.forEach(async (doc) => {
            await deleteDoc(doc.ref);
          });
        }
      } else {
        // Si no está en el calendario, agregarlo
        await addHolidayToCalendar({
          date: holiday.date,
          name: holiday.name,
          type: holiday.type,
          description: holiday.description,
          isRecurring: holiday.isRecurring,
          addedToCalendar: true,
          year: selectedYear
        });

        // También agregar a storeExceptions
        await addDoc(collection(db, 'storeExceptions'), {
          date: holiday.date,
          isOpen: false, // Los feriados están cerrados por defecto
          openTime: null,
          closeTime: null,
          reason: holiday.name,
          isHolidayException: true // Marcar como excepción de feriado
        });
      }
    } catch (error) {
      console.error('Error updating holiday calendar status:', error);
    }
  };

  const getHolidayTypeColor = (type: string) => {
    switch (type) {
      case 'national': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'regional': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'local': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const getHolidayTypeText = (type: string) => {
    switch (type) {
      case 'national': return 'Nacional';
      case 'regional': return 'Regional';
      case 'local': return 'Local';
      default: return 'Desconocido';
    }
  };

  // Combinar feriados locales con los de Firebase
  const getCombinedHolidays = (): Holiday[] => {
    // Primero, mapear los feriados locales (nacionales) con su estado de Firebase
    const nationalHolidays = localHolidays.map(localHoliday => {
      const firebaseHoliday = firebaseHolidays.find(fh => 
        fh.date === localHoliday.date && fh.name === localHoliday.name
      );
      
      return {
        ...localHoliday,
        addedToCalendar: firebaseHoliday ? firebaseHoliday.addedToCalendar : false
      };
    });

    // Luego, agregar los feriados personalizados de Firebase que no están en la lista nacional
    const customHolidays = firebaseHolidays.filter(firebaseHoliday => {
      // Solo incluir feriados que no están en la lista nacional
      return !localHolidays.some(localHoliday => 
        localHoliday.date === firebaseHoliday.date && localHoliday.name === firebaseHoliday.name
      );
    }).map(firebaseHoliday => ({
      ...firebaseHoliday,
      addedToCalendar: true // Los feriados personalizados siempre están "agregados al calendario"
    }));

    // Combinar ambos arrays
    return [...nationalHolidays, ...customHolidays];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('holidays')} - {getCountryName(country)}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">{t('automaticHolidayManagement')}</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-200 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-50 min-w-[80px]"
          >
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() + i;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </select>
          <button
            onClick={() => setShowCustomHolidayForm(true)}
            className="btn-secondary flex items-center"
          >
            <CalendarPlus className="w-5 h-5 mr-2" />
            {t('addHoliday')}
          </button>
        </div>
      </div>

      {/* Status */}
      {lastSync && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <span className="text-sm text-green-700 dark:text-green-300">
              {t('lastSynchronization')}: {formatDate(new Date(lastSync))}
            </span>
          </div>
        </div>
      )}

      {/* Custom Holiday Form */}
      {showCustomHolidayForm && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {editingHoliday ? t('editCustomHoliday') : t('addCustomHoliday')}
          </h3>
          
          <form onSubmit={handleAddCustomHoliday} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('holidayName')}
                </label>
                <input
                  type="text"
                  value={customHolidayForm.name}
                  onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, name: e.target.value })}
                  className="input-field"
                  placeholder="Ej: Aniversario del Local"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha
                </label>
                <input
                  type="date"
                  value={customHolidayForm.date}
                  onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, date: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo
              </label>
              <select
                value={customHolidayForm.type}
                onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, type: e.target.value as 'national' | 'regional' | 'local' })}
                className="input-field"
              >
                <option value="local">Local</option>
                <option value="regional">Regional</option>
                <option value="national">Nacional</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción
              </label>
              <textarea
                value={customHolidayForm.description}
                onChange={(e) => setCustomHolidayForm({ ...customHolidayForm, description: e.target.value })}
                className="input-field"
                rows={3}
                placeholder="Descripción del feriado..."
              />
            </div>

            <div className="flex gap-3">
              <button type="submit" className="btn-primary">
                {editingHoliday ? t('updateHoliday') : t('addHoliday')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCustomHolidayForm(false);
                  setCustomHolidayForm({ name: '', date: '', description: '', type: 'local' });
                  setEditingHoliday(null);
                }}
                className="btn-secondary"
              >
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Holidays List */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
          {t('nationalHolidays')} {selectedYear}
        </h3>
        
        {isLoadingLocal ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
            <span className="text-gray-600 dark:text-gray-400">{t('loadingHolidays')}</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getCombinedHolidays().map((holiday) => (
              <div
                key={holiday.id}
                className={`bg-white dark:bg-gray-700 border rounded-lg p-4 hover:shadow-md transition-shadow ${
                  holiday.addedToCalendar 
                    ? 'border-orange-300 dark:border-orange-600 bg-orange-50 dark:bg-orange-900/20' 
                    : 'border-gray-200 dark:border-gray-600'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(new Date(holiday.date))}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getHolidayTypeColor(holiday.type)}`}>
                      {getHolidayTypeText(holiday.type)}
                    </span>
                    
                    {/* Botón de toggle calendario (+ o -) */}
                    <button
                      onClick={() => handleToggleCalendar(holiday)}
                      className={`p-1 rounded-full transition-colors ${
                        holiday.addedToCalendar
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                      }`}
                      title={holiday.addedToCalendar ? 'Quitar del calendario' : 'Agregar al calendario'}
                    >
                      {holiday.addedToCalendar ? <Minus className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    </button>

                    {/* Botones de editar y eliminar (solo para feriados personalizados) */}
                    {(() => {
                      const isCustomHoliday = !localHolidays.some(localHoliday => 
                        localHoliday.date === holiday.date && localHoliday.name === holiday.name
                      );
                      
                      if (isCustomHoliday) {
                        return (
                          <>
                            <button
                              onClick={() => handleEditHoliday(holiday)}
                              className="text-primary-600 hover:text-primary-800"
                              title="Editar feriado"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteHoliday(holiday)}
                              className="text-red-600 hover:text-red-800"
                              title="Eliminar feriado"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
                
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {holiday.name}
                </h4>
                
                {holiday.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {holiday.description}
                  </p>
                )}
                
                <div className="mt-3 flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {holiday.isRecurring ? 'Recurrente' : 'Único'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
              {t('holidayInformation')}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-400">
              {t('nationalHolidaysSyncAutomatically')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
